import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiLink } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import SideBar from "../../components/sidebar/Sidebar";
import UserLoginData from "../Auth/UserLoginData";
import { useListHook } from "../Admin/List/hook";
import { useUpdateStatus } from "../Admin/Modal/ClientsAssign/hook";
import { listShortUrlsApiPO } from "./api";
import PaginatedList from "./PaginatedList";

export const ListShortUrls = () => {
  const { isLoggedIn } = UserLoginData();
  const { clientList = [] } = useListHook();
  const { defaultData } = useUpdateStatus(isLoggedIn?.response?._id);
  const navigate = useNavigate();
  const role = isLoggedIn?.response?.role || "admin";
  const clientOptions = role === "admin" ? clientList : defaultData;
  const visibleClients = Array.isArray(clientOptions) ? clientOptions : [];

  const [searchParams, setSearchParams] = useSearchParams();
  const storageKey = `shortlinks.clientId.${isLoggedIn?.response?._id || role}`;
  let savedClientId = "all";
  try {
    savedClientId = localStorage.getItem(storageKey) || "all";
  } catch {}
  const clientId = searchParams.get("clientId") || savedClientId;
  const [links, setLinks] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 12 });

  useEffect(() => {
    if (role === "client") return;
    try {
      localStorage.setItem(storageKey, clientId);
    } catch {}
    if (!searchParams.get("clientId")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("clientId", clientId);
      setSearchParams(nextParams, { replace: true });
    }
  }, [clientId, role, searchParams, setSearchParams, storageKey]);

  useEffect(() => {
    let isCurrentRequest = true;
    setLoading(true);

    const requestClientId = role === "client"
      ? isLoggedIn?.response?._id
      : role === "operational"
        ? (clientId === "all" ? (isLoggedIn?.response?.clients || []) : [clientId])
        : clientId;

    const fetchLinks = async () => {
      const [data, error] = await listShortUrlsApiPO(
        requestClientId,
        role,
        pagination.pageIndex,
        pagination.pageSize
      );

      if (!isCurrentRequest) return;
      if (error) {
        toast.error(error.response?.data?.message || "Could not load short links.");
        setLinks([]);
        setRowCount(0);
      } else {
        setLinks(data?.urls || []);
        setRowCount(data?.count || 0);
      }
      setLoading(false);
    };

    fetchLinks();
    return () => {
      isCurrentRequest = false;
    };
  }, [clientId, isLoggedIn, pagination.pageIndex, pagination.pageSize, role]);

  const handleClientChange = (event) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("clientId", event.target.value);
    setSearchParams(nextParams, { replace: true });
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const handleCopy = async (shortCode) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/${shortCode}`);
      toast.success("Short link copied");
    } catch {
      toast.error("Could not copy the short link.");
    }
  };

  const handleOpen = (shortCode) => {
    navigate(`/list/view/${encodeURIComponent(shortCode)}`);
  };

  return (
    <SideBar>
      <section className="shortlinks-page" aria-labelledby="shortlinks-title">
        <header className="shortlinks-page-header">
          <span className="create-page-icon" aria-hidden="true"><FiLink /></span>
          <div>
            <h1 id="shortlinks-title">All short links</h1>
            <p>Manage and track your links.</p>
          </div>
        </header>

        {role !== "client" && (
          <div className="shortlinks-toolbar">
            <label htmlFor="shortlinks-client-filter">Client</label>
            <select id="shortlinks-client-filter" value={clientId} onChange={handleClientChange}>
              <option value="all">All clients</option>
              {visibleClients.map((client) => (
                <option key={client._id} value={client._id}>{client.username}</option>
              ))}
            </select>
          </div>
        )}

        <PaginatedList
          links={links}
          loading={loading}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          onCopy={handleCopy}
          onOpen={handleOpen}
        />
      </section>
    </SideBar>
  );
};